<template>
  <div class="contact-about-settings">
    <h1>关于我们联系我们设置</h1>
    <div class="settings-container">
      <div class="content-setting">
        <h3>公司介绍内容</h3>
        <div class="about-us-content">
          <div class="form-group">
            <label>公司介绍（中文）</label>
            <textarea v-model="aboutUsContent.intro" placeholder="请输入公司介绍内容" rows="6"></textarea>
          </div>
          <div class="form-group">
            <label>公司介绍（英文）</label>
            <textarea v-model="aboutUsContent.introEn" placeholder="Company Introduction (English)" rows="6"></textarea>
          </div>
          <div class="logo-explain-section">
            <label>Logo 解释（中文）：</label>
            <textarea v-model="aboutUsContent.logo_explain" placeholder="请输入Logo解释" rows="3"></textarea>
          </div>
          <div class="logo-explain-section">
            <label>Logo 解释（英文）：</label>
            <textarea v-model="aboutUsContent.logoExplainEn" placeholder="Logo Explanation (English)" rows="3"></textarea>
          </div>
          <div class="values-section">
            <label>价值观（中文）：</label>
            <textarea v-model="aboutUsContent.values" placeholder="请输入价值观" rows="3"></textarea>
          </div>
          <div class="values-section">
            <label>价值观（英文）：</label>
            <textarea v-model="aboutUsContent.valuesEn" placeholder="Company Values (English)" rows="3"></textarea>
          </div>
          <div class="color-meanings-section">
            <h4>色彩寓意：</h4>
            <div v-for="(color, index) in aboutUsContent.color_meanings" :key="color.id || index">
              <div class="color-meaning-item">
                <div class="color-input-container">
                  <input type="color" v-model="color.color" class="color-picker">
                  <input type="text" v-model="color.color" placeholder="颜色值" class="color-value-input">
                </div>
                <textarea v-model="color.desc" placeholder="寓意描述（中文）" rows="2" class="desc-input"></textarea>
                <textarea v-model="color.descEn" placeholder="寓意描述（英文）" rows="2" class="desc-input"></textarea>
                <button @click="removeColorMeaningItem(index)" class="remove-btn">删除</button>
              </div>
            </div>
            <button @click="addColorMeaningItem" class="add-btn">添加色彩寓意</button>
          </div>
          <button @click="saveAboutUsContent" class="save-btn">保存内容</button>
        </div>
      </div>

      <div class="image-setting">
        <h3>公司大楼图片</h3>
        <div class="current-image">
          <img :src="companyBuildingImageUrl" alt="公司大楼图片">
        </div>
        <div class="upload-form">
          <input type="file" @change="handleCompanyBuildingImageUpload" accept="image/*">
          <button @click="uploadCompanyBuildingImage" :disabled="!companyBuildingImageFile">上传</button>
        </div>
      </div>
    </div>

    <!-- 新增留言管理部分 -->
    <div class="messages-container">
      <h2>留言管理</h2>
      <div v-if="loading" class="loading-indicator">加载中...</div>
      <div v-else-if="messages.length === 0" class="no-messages">暂无留言</div>
      <div v-else class="messages-list">
        <div class="message-item" v-for="msg in messages" :key="msg.id">
          <div class="message-header">
            <div class="message-info">
              <span class="message-name">{{ msg.name }}</span>
              <span class="message-email">{{ msg.email }}</span>
              <span class="message-phone" v-if="msg.phone">{{ msg.phone }}</span>
            </div>
            <div class="message-actions">
              <button @click="markAsRead(msg.id)" :disabled="msg.isRead" class="read-btn">
                {{ msg.isRead ? '已读' : '标记为已读' }}
              </button>
              <button @click="handleDeleteMessage(msg.id)" class="delete-btn">删除</button>
            </div>
          </div>
          <div class="message-time">{{ msg.createdAt }}</div>
          <div class="message-content">{{ msg.message }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { getContactAboutImages, updateContactAboutImage, getAboutUsContent, updateAboutUsContent, addColorMeaning, deleteColorMeaning, updateColorMeaning } from '@/api/contactAbout';
import { getMessages, markMessageAsRead, deleteMessage } from '@/api/message';

// 图片URL状态
const companyBuildingImageUrl = ref('');

// 图片文件状态
const companyBuildingImageFile = ref(null);

// 留言相关状态
const messages = ref([]);
const loading = ref(false);

// 关于我们内容状态
const aboutUsContent = ref({
  intro: '',
  introEn: '',
  logo_explain: '',
  logoExplainEn: '',
  values: '',
  valuesEn: '',
  color_meanings: []
});

// 加载留言数据
const loadMessages = async () => {
  loading.value = true;
  try {
    const response = await getMessages();
    messages.value = response.messages || [];
  } catch (error) {
    console.error('加载留言失败:', error);
  } finally {
    loading.value = false;
  }
};

// 标记留言为已读
const markAsRead = async (messageId) => {
  try {
    await markMessageAsRead(messageId);
    // 更新本地留言状态
    const msgIndex = messages.value.findIndex(msg => msg.id === messageId);
    if (msgIndex !== -1) {
      messages.value[msgIndex].isRead = true;
    }
  } catch (error) {
    console.error('标记留言为已读失败:', error);
  }
};

// 删除留言
const handleDeleteMessage = async (messageId) => {
  try {
    await deleteMessage(messageId);
    // 从本地移除留言
    messages.value = messages.value.filter(msg => msg.id !== messageId);
  } catch (error) {
    console.error('删除留言失败:', error);
  }
};

// 存储键名
const STORAGE_KEY = 'contactAboutImages';

// 从localStorage加载数据
const loadFromLocalStorage = () => {
  const savedData = localStorage.getItem(STORAGE_KEY);
  if (savedData) {
    const { companyBuilding } = JSON.parse(savedData);
    companyBuildingImageUrl.value = companyBuilding || '/assets/placeholder.png';
    return true;
  }
  return false;
};

// 保存到localStorage
const saveToLocalStorage = () => {
  const data = {
    companyBuilding: companyBuildingImageUrl.value
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// 监听数据变化自动保存
watch([companyBuildingImageUrl], () => {
  saveToLocalStorage();
});

// 加载关于我们内容
const loadAboutUsContent = async () => {
  try {
    const response = await getAboutUsContent();
    // 修改为访问 response.data.data
    const data = response.data?.data || {};
    aboutUsContent.value = {
      intro: data.intro || '',
      introEn: data.introEn || '',
      logo_explain: data.logo_explain || '',
      logoExplainEn: data.logoExplainEn || '',
      values: data.values || '',
      valuesEn: data.valuesEn || '',
      color_meanings: data.color_meanings || []
    };
  } catch (error) {
    console.error('加载关于我们内容失败:', error);
    // 设置默认值
    aboutUsContent.value = {
      intro: '',
      introEn: '',
      logo_explain: '',
      logoExplainEn: '',
      values: '',
      valuesEn: '',
      color_meanings: []
    };
  }
};

// 保存关于我们内容
// 更新saveAboutUsContent函数
const saveAboutUsContent = async () => {
  try {
    // 先保存基本内容
    const contentToSave = {
      intro: aboutUsContent.value.intro,
      introEn: aboutUsContent.value.introEn || '',
      logo_explain: aboutUsContent.value.logo_explain,
      logoExplainEn: aboutUsContent.value.logoExplainEn || '',
      values: aboutUsContent.value.values,
      valuesEn: aboutUsContent.value.valuesEn || ''
    };
    await updateAboutUsContent(contentToSave);
    
    // 处理颜色数据
    // 首先获取当前数据库中的颜色含义
    const response = await getAboutUsContent();
    const currentColors = response.data?.data?.color_meanings || [];
    const currentColorIds = currentColors.map(color => color.id);
    
    // 过滤出需要删除的颜色（数据库中有但前端没有的）
    const colorMeanings = aboutUsContent.value.color_meanings || [];
    const colorsToDelete = currentColorIds.filter(id => !colorMeanings.some(color => color.id === id));
    
    // 删除这些颜色
    for (const colorId of colorsToDelete) {
      await deleteColorMeaning(colorId);
    }
    
    // 添加或更新颜色含义
    for (const color of colorMeanings) {
      if (!color.id) {
        // 没有id，是新颜色，添加到数据库
        await addColorMeaning({
          color: color.color,
          desc: color.desc || '',
          descEn: color.descEn || ''
        });
      } else {
        // 有id，是已有颜色，更新到数据库
        await updateColorMeaning(color.id, {
          color: color.color,
          desc: color.desc || '',
          descEn: color.descEn || ''
        });
      }
    }
    
    // 重新加载数据以确保前端显示最新状态
    await loadAboutUsContent();
    
    alert('保存成功');
  } catch (error) {
    console.error('保存关于我们内容失败:', error);
    alert('保存失败，请重试');
  }
};

// 添加色彩寓意
const addColorMeaningItem = () => {
  // 确保 color_meanings 数组存在
  if (!aboutUsContent.value.color_meanings) {
    aboutUsContent.value.color_meanings = [];
  }
  aboutUsContent.value.color_meanings.push({
    color: '',
    desc: '',
    descEn: ''
  });
};

// 移除色彩寓意
const removeColorMeaningItem = (index) => {
  aboutUsContent.value.color_meanings.splice(index, 1);
};

// 加载图片数据
onMounted(async () => {
  // 优先从localStorage加载
  loadFromLocalStorage();
  
  // 从API获取图片数据
  try {
    const response = await getContactAboutImages();
    companyBuildingImageUrl.value = response.data.companyBuildingImageUrl || '/assets/placeholder.png';
    // 保存到localStorage
    saveToLocalStorage();
  } catch (error) {
    console.error('加载图片数据失败:', error);
    // 设置默认图片
    companyBuildingImageUrl.value = '/assets/placeholder.png';
  }
  
  // 加载关于我们内容
  await loadAboutUsContent();
  
  // 加载留言数据
  await loadMessages();
});

// 处理公司大楼图片上传
const handleCompanyBuildingImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    companyBuildingImageFile.value = file;
    // 创建临时URL用于预览
    const tempUrl = URL.createObjectURL(file);
    companyBuildingImageUrl.value = tempUrl;
  }
};

// 上传公司大楼图片
const uploadCompanyBuildingImage = async () => {
  if (!companyBuildingImageFile.value) return;

  try {
    const formData = new FormData();
    formData.append('file', companyBuildingImageFile.value);
    formData.append('type', 'companyBuilding');

    const response = await updateContactAboutImage(formData);
    // 上传成功后保存实际路径
    companyBuildingImageUrl.value = response.data.companyBuildingImageUrl || '/assets/placeholder.png';
    companyBuildingImageFile.value = null;
    alert('上传成功');
  } catch (error) {
    console.error('上传失败:', error);
    alert('上传失败，请重试');
  }
};
</script>

<style scoped lang="scss">
.contact-about-settings {
  padding: 20px;
}

.settings-container {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.content-setting {
  width: 45%;
  min-width: 300px;
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.image-setting {
  width: 45%;
  min-width: 300px;
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.current-image {
  margin-bottom: 20px;
  text-align: center;
}

.current-image img {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.upload-form {
  display: flex;
  gap: 10px;
}

.upload-form input {
  flex: 1;
}

.upload-form button {
  padding: 8px 16px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.upload-form button:hover {
  background-color: #389e72;
}

.upload-form button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

// 关于我们内容设置样式
.about-us-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.about-us-content textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
}

.logo-explain-section,
.values-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.logo-explain-section label,
.values-section label {
  font-weight: bold;
  color: #333;
}

.color-meanings-section {
  margin-top: 10px;
}

.color-meanings-section h4 {
  margin-bottom: 10px;
  color: #333;
}

.color-meaning-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.color-input {
  width: 100px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.desc-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
}

.add-btn,
.remove-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.add-btn {
  background-color: #42b983;
  color: white;
  align-self: flex-start;
}

.add-btn:hover {
  background-color: #389e72;
}

.remove-btn {
  background-color: #ff4d4f;
  color: white;
  white-space: nowrap;
}

.remove-btn:hover {
  background-color: #ff7875;
}

.save-btn {
  padding: 10px 20px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 10px;
}

.save-btn:hover {
  background-color: #1976d2;
}

// 留言管理样式
.messages-container {
  margin-top: 40px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #666;
}

.no-messages {
  text-align: center;
  padding: 20px;
  color: #666;
}

.messages-list {
  margin-top: 15px;
}

.message-item {
  padding: 15px;
  margin-bottom: 15px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.message-info {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.message-name {
  font-weight: bold;
  color: #333;
}

.message-email {
  color: #666;
}

.message-phone {
  color: #666;
}

.message-actions {
  display: flex;
  gap: 10px;
}

.read-btn {
  padding: 5px 10px;
  background-color: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.read-btn:disabled {
  background-color: #e0e0e0;
  cursor: not-allowed;
  color: #999;
}

.delete-btn {
  padding: 5px 10px;
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.message-time {
  color: #999;
  font-size: 12px;
  margin-bottom: 10px;
}

.message-content {
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.color-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-picker {
  width: 50px;
  height: 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

.color-value-input {
  width: 100px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: monospace;
}

.color-preview {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: transform 0.2s;
}

.color-preview:hover {
  transform: scale(1.1);
}
</style>